const INITIAL_MESSAGE = `
Soy tu agente virtual y estoy aquí para ayudarte con tus planes de viaje.
A veces utilizo IA para responder y la conversación puede grabarse para mejorar el servicio.
Para empezar, escribe tu pregunta.
`;

export default function createChatbotModule(AJAX_CONVERSATION_URLS, milgrams_mode = false) {
	return {
		chatBotModal: {
			open: false,
			initialized: false,
            initializing: false,
			show_hint: localStorage.getItem('chatbot_hint_closed') === null ? true : false,
			slide_hint: false,
			conversation: [],
			waiting_for_response: false,
			current_message: '',
			error: null,
			waiting_for_response_timeout: null,
			waiting_for_response_message: null,
			waiting_for_response_interval: null,
			chat_start_sent: false,
			show_privacy_notice: true,
		},

		async getOrCreateConversation() {
			this.chatBotModal.error = null;

			// Check session storage if some conversation has been started
			const conversation_started = sessionStorage.getItem('chatbot_conversation_started');
            let conversation = [];
			if (conversation_started) {
				// Fetch the conversation from the server
				let response;
				try {
					response = await fetch(AJAX_CONVERSATION_URLS.get_conversation.replace(':conversation_id', conversation_started));
				} catch (error) {
					console.error(error);
					this.chatBotModal.error = "Ha ocurrido un error al obtener la conversación. Por favor, inténtalo de nuevo.";
					return;
				}

				if (response.status !== 200) {
					this.chatBotModal.error = "Ha ocurrido un error al obtener la conversación. Por favor, inténtalo de nuevo.";
					return;
				}

				const response_json = await response.json();

				if (response_json.error) {
					this.chatBotModal.error = response_json.error;
					return;
				}

                conversation = response_json.messages;
				this.chatBotModal.show_privacy_notice = !conversation.some(
					(message) => message.role === 'user'
				);

				// Add initial message to the start of the conversation
				conversation.unshift({
					role: 'assistant',
					content: INITIAL_MESSAGE,
				});
			} else {
				// Add initial message to the start of the conversation
				conversation.unshift({
					role: 'assistant',
					content: INITIAL_MESSAGE,
				});
				this.chatBotModal.conversation = conversation;
				this.chatBotModal.initializing = false;  // fake, we want to hide that from the user

				const user_uuid = document.cookie.split('; ').find(row => row.startsWith('user_uuid='))?.split('=')[1];

				// Create a new conversation
				const response = await fetch(AJAX_CONVERSATION_URLS.create_conversation, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						landing_page: window.location.pathname,
						user_uuid: user_uuid,
						timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					}),
				});

				if (response.status !== 201) {
					this.chatBotModal.error = "Ha ocurrido un error al crear la conversación. Por favor, inténtalo de nuevo.";
					return;
				}

				const response_json = await response.json();

				if (response_json.error) {
					this.chatBotModal.error = response_json.error;
					return;
				}

				sessionStorage.setItem('chatbot_conversation_started', response_json.conversation_uuid);
			}

            conversation = conversation.map(message => ({
                role: message.role,
                content: this.formatMessage(message.content),
            }));

            // Extend the conversation with the messages from the server
            this.chatBotModal.conversation = conversation;
		},

		async toggleChatBotModal() {
			// Open the ChatBot modal (embed from milgrams.cat)

			// If we open the modal we want to make sure the hint is not shown
			this.closeChatBotHint();

			this.chatBotModal.open = !this.chatBotModal.open;

			if (this.chatBotModal.open && !this.chatBotModal.chat_start_sent) {
				this.chatBotModal.chat_start_sent = true;
				try {
					dataLayer.push({
						'event': 'chat_start',
						'event_category': 'Lead Generation',
						'event_label': 'Chat Started',
					});
					
				} catch (error) {
					console.error("Error al enviar evento chat_start a dataLayer:", error);
				}
			}


			if (!this.chatBotModal.initialized && !this.chatBotModal.initializing) {
                this.chatBotModal.initializing = true;
				// Add the initial message to the conversation

				try {
					await this.getOrCreateConversation();
				} catch (error) {
                    console.error(error);
					this.chatBotModal.error = "Ha ocurrido un error al crear la conversación. Por favor, intenta de nuevo en unos minutos.";
					this.chatBotModal.initializing = false;
					return;
				}

				this.chatBotModal.initialized = true;
				this.chatBotModal.initializing = false;
			}

			if (this.chatBotModal.open) {
				setTimeout(() => {
					const input = document.getElementById('chatbot-user-input');
					if (input) {
						input.focus();
						// Set the current message content if it exists
						if (this.chatBotModal.current_message) {
							input.textContent = this.chatBotModal.current_message;
						}
					}
				}, 100);
				this.scrollToBottom();
			}
		},

		scrollToBottom() {
			const modal = document.getElementById("chatbot-modal");
			const chatbotBody = modal.querySelector('.chatbot-body');

			// Allow alpine time to update the DOM
			setTimeout(() => {
				chatbotBody.scrollTo({
					top: chatbotBody.scrollHeight,
					behavior: 'smooth'
				});
			}, 200);
		},

        formatMessage(message) {
			let formatted_message = message.trim();
			const openInNewTabAttrs = 'target="_blank" rel="noopener noreferrer"';
			const markdownLinkPlaceholders = [];

			// Format lists "- text" to <li>text</li> and wrap around <ul>
			formatted_message = formatted_message.replace(
				/(?:^|\n)((?:-\s+.+(?:\n|$))+)/g,
				(_match, listBlock) => {
					const items = listBlock
						.trim()
						.split('\n')
						.map((line) => line.replace(/^\s*-\s+/, '').trim())
						.filter(Boolean)
						.map((item) => `<li>${item}</li>`)
						.join('');
					return `\n<ul>${items}</ul>`;
				}
			);

			// Format Markdown links first ([text](url)) and keep placeholders
			// so plain-url replacement does not touch generated anchor URLs.
			formatted_message = formatted_message.replace(
				/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
				(_match, text, url) => {
					const placeholder = `__MD_LINK_${markdownLinkPlaceholders.length}__`;
					markdownLinkPlaceholders.push(`<a href="${url}" ${openInNewTabAttrs}>${text}</a>`);
					return placeholder;
				}
			);

			// Format plain urls to clickable links
			formatted_message = formatted_message.replace(
				/(https?:\/\/[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9]{1,6}\b[-a-zA-Z0-9()@:%_+.~#?&/=]*)/g,
				`<a href="$1" ${openInNewTabAttrs}>$1</a>`
			);

			// Restore markdown links
			formatted_message = formatted_message.replace(/__MD_LINK_(\d+)__/g, (_match, index) => {
				return markdownLinkPlaceholders[Number(index)] ?? _match;
			});

			// Format Markdown bold text
			formatted_message = formatted_message.replace(/(\*\*)(.*?)(\*\*)/g, '<strong>$2</strong>');
			// Format Markdown italic text
			formatted_message = formatted_message.replace(/(\*)(.*?)(\*)/g, '<em>$2</em>');

			// Format newlines to <br> tags
			formatted_message = formatted_message.replace(/\n+/g, '<br>');

            return formatted_message;
        },

		async errorRecieved(message, error_message = "Ha ocurrido un error al enviar el mensaje. Por favor, inténtalo de nuevo.") {
			const input = document.getElementById('chatbot-user-input');
			// Recover message if something failed
			this.chatBotModal.current_message = message;
			this.chatBotModal.conversation.pop();
			// Restore the contenteditable div content
			if (input) {
				input.innerHTML = message;
			}
			this.chatBotModal.error = error_message;
		},

		async startWaitingForResponse() {
			const second = 1000;
			// Should already be true, but just in case
			this.chatBotModal.waiting_for_response = true;
			
			// Array of messages to cycle through
			const waitingMessages = [
				"Estoy consultando algunos datos, dame unos segundos...",
				"Sigo trabajando en tu consulta...",
				"Todavía estoy procesando la información...",
				"Un momento más, casi termino...",
			];
			
			let messageIndex = 0;
			
			// Set initial message after 5 seconds
			this.chatBotModal.waiting_for_response_timeout = setTimeout(() => {
				this.chatBotModal.waiting_for_response_message = waitingMessages[messageIndex];
				queueMicrotask(() => this.scrollToBottom());
				messageIndex = (messageIndex + 1) % waitingMessages.length;
				
				// Then cycle through messages every 5 seconds
				this.chatBotModal.waiting_for_response_interval = setInterval(() => {
					this.chatBotModal.waiting_for_response_message = waitingMessages[messageIndex];
					queueMicrotask(() => this.scrollToBottom());
					messageIndex = (messageIndex + 1) % waitingMessages.length;
				}, 5 * second);
			}, 5 * second);
		},

		async stopWaitingForResponse() {
			this.chatBotModal.waiting_for_response = false;
			
			// Clear the timeout if it hasn't fired yet
			if (this.chatBotModal.waiting_for_response_timeout) {
				clearTimeout(this.chatBotModal.waiting_for_response_timeout);
				this.chatBotModal.waiting_for_response_timeout = null;
			}
			
			// Clear the interval if it's running
			if (this.chatBotModal.waiting_for_response_interval) {
				clearInterval(this.chatBotModal.waiting_for_response_interval);
				this.chatBotModal.waiting_for_response_interval = null;
			}
			
			// Clear the message
			this.chatBotModal.waiting_for_response_message = null;
		},

		async sendMessageToChatBot() {
			if (!this.chatBotModal.current_message) return;
			if (this.chatBotModal.current_message.length === 0) return;
			if (this.chatBotModal.open === false) return;
			if (this.chatBotModal.waiting_for_response) return;
			this.chatBotModal.waiting_for_response = true;

			const message = this.chatBotModal.current_message;
			this.chatBotModal.current_message = '';

			// Clear the contenteditable div
			const input = document.getElementById('chatbot-user-input');
			if (input) {
				input.innerHTML = '';
			}

			// Add the message to the conversation
			this.chatBotModal.conversation.push({
				role: 'user',
				content: message,
			});
			this.chatBotModal.show_privacy_notice = false;

			this.scrollToBottom();
			this.chatBotModal.error = null;

			// If we have not initialized the chatbot, wait for it
			if (this.chatBotModal.initialized === false) {
				while (this.chatBotModal.initialized === false) {
					if (this.chatBotModal.initializing === false) {
						this.errorRecieved(message);
						this.chatBotModal.waiting_for_response = false;
						return;
					}
					await new Promise((resolve) => setTimeout(resolve, 100));
				}
			}

			let response;
			try {
				this.startWaitingForResponse();
				response = await fetch(AJAX_CONVERSATION_URLS.send_message.replace(':conversation_id', sessionStorage.getItem('chatbot_conversation_started')), {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						message: message,
					}),
				});
			} catch (error) {
				console.error("Error sending message to chatbot:", error);

				this.errorRecieved(message);
				return;
			} finally {
				this.stopWaitingForResponse();
			}

			let response_json;
			try {
				response_json = await response.json();
			} catch (error) {
				response_json = null;
			}


			if (response.status !== 200) {
				if (response_json && response_json.code) {
					switch (response_json.code) {
						case "conversation_limit":
							this.errorRecieved(
								message,
								`Esta conversación ha llegado a su límite.
								Pudes iniciar otra conversación en una pestaña diferente.`,
							);
							return;
						case "time_limit":
							this.errorRecieved(
								message,
								`Has enviado muchos mensajes ràpidamente.
								Por favor, espera unos segundos y prueba de nuevo.`,
							);
							return;
						default:
							this.errorRecieved(message);
							return;
					}
				}
				this.errorRecieved(message);
				return;
			}


			this.chatBotModal.conversation.push({
				role: 'assistant',
				content: this.formatMessage(response_json.message),
			});

			this.scrollToBottom();
		},

		async initChatBotHint() {
            if (milgrams_mode) return;  // Prevent showing two hints when milgrams is enabled
			await new Promise((resolve) => setTimeout(resolve, 5000));
			this.chatBotModal.slide_hint = true;
		},

		closeChatBotHint() {
			localStorage.setItem('chatbot_hint_closed', true);
			this.chatBotModal.show_hint = false;
		},

		updateChatbotMessage(target) {
			// If only element is a <br> remove it
			if (target.children.length === 1 && target.children[0].tagName === 'BR') {
				target.innerHTML = '';
				return;
			}

			let textContent = target.textContent;
			// Limit to 255 characters
			if (textContent.length > 255) {
				textContent = textContent.substring(0, 255);
				// Update the contenteditable div to reflect the truncated text
				this.$nextTick(() => {
					const input = document.getElementById('chatbot-user-input');
					if (input) {
						input.innerHTML = textContent;
						// Place cursor at the end
						const range = document.createRange();
						const selection = window.getSelection();
						range.selectNodeContents(input);
						range.collapse(false);
						selection.removeAllRanges();
						selection.addRange(range);
					}
				});
			}
			this.chatBotModal.current_message = textContent;
		},

		handlePaste(event) {
			event.preventDefault();
			const text = event.clipboardData.getData('text/plain');
			document.execCommand('insertText', false, text);
		},
	};
} 